import React, { useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import BaseButton from '../../components/BasicComponent/BaseButton'
import FixedBottom from '../../components/BasicComponent/FixedBottom'
import HeaderPartner from '../../components/FeaturesComponent/headerPartner'
import LayoutPartner from '../../components/FeaturesComponent/layoutPartner'
import AutomatedTrafficFineNotificationStep from '../../components/FeaturesComponent/automatedTrafficFineNotificationStep'
import TaxSearchForm from './components/TaxSearchForm'
import TaxEmptyResult from './components/TaxEmptyResult'
import MstResultList from './components/MstResultList'
import { TAX_SEARCH_TYPE, validateTaxKeyword } from './constants/taxLookup'
import TaxCdsService from '../../services/taxCdsService'
import {
  getTaxLookupResponseCache,
  saveTaxLookupResponseCache
} from '../../helper/taxLookupCache'
import { PATH } from '../../constants/router'
import './index.scss'

const TAX_SMART_ASSISTANT_MST_LOOKUP_CACHE_KEY = 'TAX_SMART_ASSISTANT_MST_LOOKUP_CACHE'
const TAX_SMART_ASSISTANT_MST_LOOKUP_RESPONSE_CACHE_KEY = 'TAX_SMART_ASSISTANT_MST_LOOKUP_RESPONSE_CACHE'

const getInitialLookupCache = () => {
  try {
    const cachedLookup = localStorage.getItem(TAX_SMART_ASSISTANT_MST_LOOKUP_CACHE_KEY)

    if (cachedLookup) {
      const parsedLookup = JSON.parse(cachedLookup)
      return Array.isArray(parsedLookup) ? parsedLookup : []
    }

    return []
  } catch (error) {
    return []
  }
}

const saveLookupCacheToLocalStorage = (lookupList = []) => {
  try {
    localStorage.setItem(TAX_SMART_ASSISTANT_MST_LOOKUP_CACHE_KEY, JSON.stringify(lookupList))
  } catch (error) {
    // ignore localStorage error
  }
}

const buildMstLookupFilter = ({ searchType, keyword }) => {
  const searchKeyword = keyword.trim()

  if (searchType === TAX_SEARCH_TYPE.MST) {
    return {
      taxCode: searchKeyword
    }
  }

  if (searchType === TAX_SEARCH_TYPE.CCCD) {
    return {
      citizenIdentityNumber: searchKeyword
    }
  }

  return {
    taxpayerName: searchKeyword
  }
}

const getMstLookupKey = (item = {}) => {
  return item.taxCode || item.citizenIdentityNumber || item.taxpayerName || ''
}

const mapTaxLookupResult = (item = {}) => {
  return {
    taxCDSMstLookupId: item.taxCDSMstLookupId || null,
    taxCode: item.taxCode || '',
    citizenIdentityNumber: item.citizenIdentityNumber || '',
    taxpayerName: item.taxpayerName || '',
    businessAddress: item.businessAddress || '',
    taxRegistrationAddress: item.taxRegistrationAddress || '',
    taxDepartment: item.taxDepartment || '',
    status: item.status || 'UNKNOWN',
    statusText: item.statusText || '',
    note: item.note || '',
    isRegistered: Boolean(item.isRegistered)
  }
}

const mapTaxLookupResults = (items = []) => {
  return items.map(mapTaxLookupResult)
}

export default function TaxCDSSmartAssistantInfo() {
  const history = useHistory()
  const location = useLocation()
  const selectedServices = location?.state?.services || []

  const [searchType, setSearchType] = useState(TAX_SEARCH_TYPE.MST)
  const [keyword, setKeyword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [results, setResults] = useState([])
  const [selectedTaxInfo, setSelectedTaxInfo] = useState(null)
  const [lookupCacheResults, setLookupCacheResults] = useState(getInitialLookupCache)
  const [lookupCacheExpanded, setLookupCacheExpanded] = useState(false)

  const filteredLookupCacheResults = useMemo(() => {
    return lookupCacheResults.filter((item) => getMstLookupKey(item))
  }, [lookupCacheResults])

  const currentResultKeys = results.map((item) => getMstLookupKey(item))

const displayLookupCacheResults = filteredLookupCacheResults.filter((item) => {
  return !currentResultKeys.includes(getMstLookupKey(item))
})

const visibleLookupCacheResults = lookupCacheExpanded
  ? displayLookupCacheResults
  : displayLookupCacheResults.slice(0, 3)

const canToggleLookupCache = displayLookupCacheResults.length > 3

const selectedTaxInfoKey = selectedTaxInfo ? getMstLookupKey(selectedTaxInfo) : ''

  const validateKeyword = () => {
    return validateTaxKeyword({
      searchType,
      keyword
    })
  }

  const saveLookupCache = (nextResults = []) => {
    if (!nextResults.length) return

    const nextLookupList = [...nextResults, ...lookupCacheResults].filter((item, index, array) => {
      const itemKey = getMstLookupKey(item)

      if (!itemKey) return false

      return array.findIndex((lookupItem) => getMstLookupKey(lookupItem) === itemKey) === index
    })

    const limitedLookupList = nextLookupList.slice(0, 10)

    setLookupCacheResults(limitedLookupList)
    saveLookupCacheToLocalStorage(limitedLookupList)
  }

  const handleDeleteLookupCache = (item) => {
    const itemKey = getMstLookupKey(item)
    const nextLookupList = lookupCacheResults.filter((lookupItem) => getMstLookupKey(lookupItem) !== itemKey)

    setLookupCacheResults(nextLookupList)
    saveLookupCacheToLocalStorage(nextLookupList)

    if (selectedTaxInfo && getMstLookupKey(selectedTaxInfo) === itemKey) {
      setSelectedTaxInfo(null)
    }
  }

  const handleSelectTaxInfo = (item) => {
    setSelectedTaxInfo(item)
  }

  const handleSubmit = async () => {
    const message = validateKeyword()

    if (message) {
      setError(message)
      setSearched(false)
      setResults([])
      setSelectedTaxInfo(null)
      return
    }

    const cachedList = getTaxLookupResponseCache({
      storageKey: TAX_SMART_ASSISTANT_MST_LOOKUP_RESPONSE_CACHE_KEY,
      searchType,
      keyword
    })

    if (cachedList) {
      const nextResults = mapTaxLookupResults(cachedList)

      setError('')
      setSearched(true)
      setResults(nextResults)
      setSelectedTaxInfo(nextResults?.[0] || null)
      setLookupCacheExpanded(false)
      saveLookupCache(nextResults)
      return
    }

    setError('')
    setSearched(false)
    setResults([])
    setSelectedTaxInfo(null)
    setLookupCacheExpanded(false)
    setLoading(true)

    try {
      const response = await TaxCdsService.lookupMst({
        filter: buildMstLookupFilter({
          searchType,
          keyword
        }),
        skip: 0,
        limit: 10
      })

      setSearched(true)

      if (!response?.isSuccess) {
        setError(response?.message || 'Không thể tra cứu thông tin thuế. Vui lòng thử lại.')
        setResults([])
        setSelectedTaxInfo(null)
        return
      }

      const list = Array.isArray(response?.data?.data) ? response.data.data : []
      const nextResults = mapTaxLookupResults(list)

      saveTaxLookupResponseCache({
        storageKey: TAX_SMART_ASSISTANT_MST_LOOKUP_RESPONSE_CACHE_KEY,
        searchType,
        keyword,
        data: list
      })

      setResults(nextResults)
      setSelectedTaxInfo(nextResults?.[0] || null)
      saveLookupCache(nextResults)
    } catch (error) {
      setSearched(true)
      setResults([])
      setSelectedTaxInfo(null)
      setError('Không thể tra cứu thông tin thuế. Vui lòng thử lại.')
    } finally {
      setLoading(false)
    }
  }

  const getTaxIdentity = (taxInfo = {}) => {
    return String(
        taxInfo.taxCode ||
        taxInfo.citizenIdentityNumber ||
        ''
    ).trim()
    }


    /**
   * TaxCDS dùng lại payment flow của gói phạt nguội.
   *
   * Payment flow hiện tại đọc danh sách đối tượng thanh toán qua key `vehicles`,
   * nên TaxCDS cần map thông tin thuế sang cùng shape này để không phải tách
   * thêm một payment flow mới.
   *
   * Mapping khác biệt:
   * - `plate`: dùng làm mã hiển thị chính trên payment, với TaxCDS sẽ là MST/CCCD.
   * - `taxCode`, `citizenIdentityNumber`, `taxpayerName`, `businessAddress`,
   *   `taxRegistrationAddress`: giữ lại để PaymentInfo render đúng thông tin thuế.
   * - `taxInfo`: giữ full response gốc để các bước sau có thể dùng nếu cần.
   */
    const handleNextStep = () => {
    if (!selectedTaxInfo) return

    const taxIdentity = getTaxIdentity(selectedTaxInfo)

    if (!taxIdentity) return

   history.push(PATH.AUTO_TRAFFIC_FINE_NOTIFICATION_PAYMENT, {
        services: selectedServices,
        vehicles: [
            {
            plate: taxIdentity,
            taxCode: selectedTaxInfo.taxCode,
            citizenIdentityNumber: selectedTaxInfo.citizenIdentityNumber,
            taxpayerName: selectedTaxInfo.taxpayerName,
            businessAddress: selectedTaxInfo.businessAddress,
            taxRegistrationAddress: selectedTaxInfo.taxRegistrationAddress
            }
        ],
        taxInfo: selectedTaxInfo
        })
    }

  return (
    <LayoutPartner>
      <HeaderPartner title="Thông tin thuế" />

      <div className="TaxCDSSmartAssistantInfo">
        <AutomatedTrafficFineNotificationStep step={1} />

        <div className="TaxCDSSmartAssistantInfo_search-title">
          Chọn 1 mã số thuế để tiếp tục
        </div>

        <div className="TaxCDSSmartAssistantInfo_search-card">
          <TaxSearchForm
            searchType={searchType}
            value={keyword}
            error={error}
            loading={loading}
            onChangeSearchType={(type) => {
              setSearchType(type)
              setKeyword('')
              setError('')
              setSearched(false)
              setResults([])
              setSelectedTaxInfo(null)
              setLookupCacheExpanded(false)
            }}
            onChangeValue={(value) => {
              setKeyword(value)
              setError('')
              setSearched(false)
              setResults([])
              setSelectedTaxInfo(null)
              setLookupCacheExpanded(false)
            }}
            onSubmit={handleSubmit}
          />

          {(loading || searched) && (
            <div className="TaxCDSSmartAssistantInfo_result">
              <div className="TaxCDSSmartAssistantInfo_result-title">
                Kết quả tìm thấy:
              </div>

              {loading ? (
                <div className="TaxCDSSmartAssistantInfo_loading">Đang tìm kiếm...</div>
              ) : results.length > 0 ? (
                <MstResultList
                    results={results}
                    onRegister={handleSelectTaxInfo}
                    selectedKey={selectedTaxInfoKey}
                    showFooter
                    />
              ) : (
                <TaxEmptyResult />
              )}
            </div>
          )}

          {filteredLookupCacheResults.length > 0 && (
            <div className="TaxCDSSmartAssistantInfo_history">
              <div className="TaxCDSSmartAssistantInfo_history-title">
                Lịch sử tra cứu
              </div>

              <MstResultList
                results={visibleLookupCacheResults}
                onRegister={handleSelectTaxInfo}
                selectedKey={selectedTaxInfoKey}
                swipeToDelete
                onDelete={handleDeleteLookupCache}
                />

              {canToggleLookupCache && (
                <button
                  type="button"
                  className="TaxCDSSmartAssistantInfo_history-toggle"
                  onClick={() => setLookupCacheExpanded((prev) => !prev)}
                >
                  <span>{lookupCacheExpanded ? 'Thu gọn' : 'Xem thêm'}</span>
                  <i
                    className={`TaxCDSSmartAssistantInfo_history-toggle-icon ${
                      lookupCacheExpanded ? 'is-expanded' : ''
                    }`}
                  />
                </button>
              )}
            </div>
          )}
        </div>

        <FixedBottom elementPaddingBottom="LayoutPartner">
          <BaseButton
            onClick={handleNextStep}
            disabled={!selectedTaxInfo || selectedServices.length === 0}
          >
            Tiếp theo
          </BaseButton>
        </FixedBottom>
      </div>
    </LayoutPartner>
  )
}